import { html, nothing, unsafeCSS } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { DERStatus } from '~/ui/@types/der-status';
import { DomUtility, SvgUtility } from '~/ui/utils';
import DERFormElement from '~/ui/utils/der-form-element';

import { styles as globalStyles } from '~/ui/scss/main.styles';
import { styles } from './checkbox.styles';

export class Checkbox extends DERFormElement {
    static override styles = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    @query('input')
    inputEl!: HTMLInputElement;

    @property({ type: String })
    inputId: string = DomUtility.uniqueElementId(this);

    @property({ type: Boolean })
    checked: boolean = false;

    @property({ type: String })
    label?: string;

    @property({ type: Boolean })
    disabled = false;

    @property({ type: Number })
    tIndex: number = 0;

    @property({ type: String })
    name: string = this.inputId;

    @property()
    value: string | null = 'true';

    @property({ type: String })
    status?: DERStatus;

    @property({ type: Boolean })
    required = false;

    constructor() {
        super();
        this.attachShadow({
            mode: 'open',
            delegatesFocus: true,
        });
    }

    override connectedCallback() {
        super.connectedCallback();
        this.updateComplete.then(() => {
            this.checkInputValidity();
        });
    }

    private checkInputValidity() {
        this.setDERValidity(
            { valueMissing: this.required && !this.checked },
            this.inputEl?.validationMessage,
            this.inputEl
        );
    }

    private getAriaInvalid(): 'true' | 'false' | undefined {
        if (this.status === 'success') {
            return 'false';
        } else if (this.status === 'error') {
            return 'true';
        }
        return undefined;
    }

    override emitEvent(name: string, detail: unknown): void {
        this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
    }

    onChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.checked = target.checked;
        const checkedValue = this.checked ? target.value : null;
        this._internals.setFormValue(checkedValue);
        this.checkInputValidity();
        this.emitEvent('der.change', {
            name: this.name,
            value: checkedValue,
            event,
        });
    }

    override render() {
        const hasLabelSlot = DomUtility.hasNamedSlotNodes(this, 'label');
        const { disabled, tIndex, inputId, checked, name, label } = this;

        return html`
            <div class="checkbox-wrapper">
                <label
                    class=${classMap({
                        'c-input': true,
                        'c-input-checkbox': true,
                        disabled: disabled,
                        error: this.status === 'error',
                        success: this.status === 'success',
                    })}
                    ?aria-hidden=${disabled}
                    for=${inputId}
                >
                    <input
                        class="input-el"
                        type="checkbox"
                        .value=${this.value}
                        ?checked=${checked}
                        ?disabled=${disabled}
                        ?required=${this.required}
                        aria-invalid=${this.getAriaInvalid() ?? nothing}
                        id=${inputId}
                        tabindex=${tIndex}
                        name=${name}
                        @change=${this.onChange}
                        aria-label=${label}
                    />
                    <span class="svg-checkbox" role="checkbox" aria-checked=${checked}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 40 40" fill="none">
                            ${SvgUtility.systemCheck}
                        </svg>
                    </span>
                    ${this.label || hasLabelSlot
                        ? html`
                              <span class="label-inline">
                                  ${!hasLabelSlot ? label : ''}
                                  <slot name="label"></slot>
                              </span>
                          `
                        : ''}
                </label>
            </div>
        `;
    }
}
